/**
 * Sentry utilities for error tracking, tracing, and logging
 * 
 * Usage:
 *   import { captureError, traceAction, traceApiCall, logger } from '@/lib/sentry';
 */
import * as Sentry from "@sentry/react";

// Get the Sentry logger instance
export const { logger } = Sentry;

/**
 * Capture an exception and log it to Sentry
 * Use in try-catch blocks or areas where exceptions are expected
 * 
 * @param {Error} error - The error to capture
 * @param {Object} context - Additional context to attach
 * 
 * @example
 * try {
 *   await riskyOperation();
 * } catch (error) {
 *   captureError(error, { userId: '123', action: 'riskyOperation' });
 * }
 */
export function captureError(error, context = {}) {
  Sentry.withScope((scope) => {
    Object.entries(context).forEach(([key, value]) => {
      scope.setExtra(key, value);
    });
    Sentry.captureException(error);
  });
}

/**
 * Create a span for UI actions like button clicks
 * 
 * @param {string} name - Name of the action (e.g., "Create Project Button Click")
 * @param {Function} callback - The action to trace
 * @param {Object} attributes - Additional attributes to attach to the span
 * 
 * @example
 * const handleCreateProject = () => {
 *   traceAction("Create Project", () => {
 *     // do something
 *   }, { projectName: 'My Project' });
 * };
 */
export function traceAction(name, callback, attributes = {}) {
  return Sentry.startSpan(
    {
      op: "ui.action",
      name,
    },
    (span) => {
      Object.entries(attributes).forEach(([key, value]) => {
        span.setAttribute(key, value);
      });
      return callback();
    }
  );
}

/**
 * Create a span for button clicks specifically
 * 
 * @param {string} buttonName - Name of the button
 * @param {Function} callback - The click handler
 * @param {Object} attributes - Additional attributes
 * 
 * @example
 * <button onClick={() => traceClick("Submit Report", handleSubmit, { reportId: '123' })}>
 *   Submit
 * </button>
 */
export function traceClick(buttonName, callback, attributes = {}) {
  return Sentry.startSpan(
    {
      op: "ui.click",
      name: `${buttonName} Click`,
    },
    (span) => {
      Object.entries(attributes).forEach(([key, value]) => {
        span.setAttribute(key, value);
      });
      return callback();
    }
  );
}

/**
 * Create a span for API calls
 * 
 * @param {string} method - HTTP method (GET, POST, etc.)
 * @param {string} endpoint - API endpoint
 * @param {Function} apiCall - Async function that makes the API call
 * 
 * @example
 * const fetchProject = async (projectId) => {
 *   return traceApiCall("GET", `/api/projects/${projectId}`, async () => {
 *     const response = await axios.get(`/api/projects/${projectId}`);
 *     return response.data;
 *   });
 * };
 */
export async function traceApiCall(method, endpoint, apiCall) {
  return Sentry.startSpan(
    {
      op: "http.client",
      name: `${method} ${endpoint}`,
    },
    async (span) => {
      try {
        const result = await apiCall();
        span.setStatus({ code: 1 }); // OK
        return result;
      } catch (error) {
        span.setStatus({ code: 2, message: error.message }); // ERROR
        throw error;
      }
    }
  );
}

/**
 * Create a span for form submissions
 * 
 * @param {string} formName - Name of the form
 * @param {Function} submitHandler - Async function that handles submission
 * @param {Object} attributes - Form data attributes to track
 * 
 * @example
 * const handleSubmit = async (data) => {
 *   return traceFormSubmit("Report Builder", async () => {
 *     await api.createReport(data);
 *   }, { projectId: data.projectId });
 * };
 */
export async function traceFormSubmit(formName, submitHandler, attributes = {}) {
  return Sentry.startSpan(
    {
      op: "ui.submit",
      name: `${formName} Submit`,
    },
    async (span) => {
      Object.entries(attributes).forEach(([key, value]) => {
        span.setAttribute(key, value);
      });
      try {
        const result = await submitHandler();
        span.setStatus({ code: 1 }); // OK
        return result;
      } catch (error) {
        span.setStatus({ code: 2, message: error.message }); // ERROR
        throw error;
      }
    }
  );
}

/**
 * Set user context for all subsequent events
 * Call this after login
 * 
 * @param {Object} user - User object with id, email, etc.
 * 
 * @example
 * setUser({ id: '123', email: 'user@example.com', name: 'John' });
 */
export function setUser(user) {
  Sentry.setUser({
    id: user.id,
    email: user.email,
    username: user.name,
  });
}

/**
 * Clear user context on logout
 */
export function clearUser() {
  Sentry.setUser(null);
}

// Re-export Sentry for advanced usage
export { Sentry };
